import { useWsEvents } from '../hooks/useWsEvents';
import { useState, useRef, useEffect, useCallback } from 'react';
import type { CardData, AnimationSettings } from '../types/widgets';

// Holding class applied between setting the new image URL and triggering
// the entry animation on the next frame. Prevents a single-frame flash
// where the image renders at full opacity before the animation starts.
const ANIM_HIDDEN = 'anim-hidden';

// TRUE FAILSAFE ONLY — not a competing trigger. The real trigger is
// img.decode() (primary) / onLoad+onError (redundant backup) on the actual
// mounted <img>, both of which resolve only once the bitmap is genuinely
// ready to paint, however long that takes (cached: ~ms, uncached/network:
// hundreds of ms or more). This timer's ONLY job is to prevent a
// permanently-invisible card if a load/decode event is somehow never
// delivered (extremely rare browser edge case). It must never be short
// enough to preempt a normal — even slow — image load, or the entry
// animation will start before the bitmap is ready, reintroducing the flash.
const ENTRY_SAFETY_DELAY_MS = 3000;

// TRUE FAILSAFE ONLY for the exit→swap transition — not a competing trigger.
// The real trigger is the exit animation's own `animationend` event on the
// card-frame element (see armExitSwap below), which fires exactly when each
// preset's actual CSS duration elapses (0.3s–0.7s depending on preset — see
// animations.css), eliminating the dead gap that existed when the swap was
// driven by a single hardcoded delay regardless of which exit was playing
// (e.g. the default fade-out exit is 0.3s, but a 700ms fixed timer left a
// ~400ms gap where the old card was already gone and the new one hadn't
// swapped in yet). This fallback only fires if animationend/animationcancel
// is never delivered by the browser (rare edge case) — kept comfortably above
// the longest configured exit preset (spin-away, 0.7s) so it never preempts a
// real animation under normal conditions.
const EXIT_FALLBACK_DELAY_MS = 800;

// Preset packs → animation IDs
const PRESET_CARD_ENTRY: Record<string, string> = {
  clean: 'fade-grow',
  dynamic: 'slide-up',
  dramatic: 'zoom-burst',
  retro: 'glide-left',
  classic: 'flip-in',
};
const PRESET_CARD_EXIT: Record<string, string> = {
  clean: 'fade-out',
  dynamic: 'zoom-out',
  dramatic: 'spin-away',
  retro: 'slide-down',
  classic: 'fade-out',
};

function resolveAnimId(
  animationSettings: AnimationSettings | null,
  slot: 'cardEntry' | 'cardExit',
): string {
  if (!animationSettings) return slot === 'cardEntry' ? 'flip-in' : 'fade-out';
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- the `animationSettings[slot]` truthiness check in this `if` condition already guarantees the value is defined (and non-empty) here.
  if (animationSettings.mode === 'custom' && animationSettings[slot]) return animationSettings[slot]!;
  const preset = animationSettings.preset ?? 'classic';
  const map = slot === 'cardEntry' ? PRESET_CARD_ENTRY : PRESET_CARD_EXIT;
  return map[preset] ?? (slot === 'cardEntry' ? 'flip-in' : 'fade-out');
}

/**
 * Arms a one-shot listener for the end of whatever exit animation is currently
 * running on `frameEl`, then calls `performSwap`. Falls back to a fixed timer
 * if no animationend/animationcancel event arrives within `fallbackMs`.
 *
 * GUARD AGAINST THE PRIOR ANIMATION'S animationcancel: if `frameEl` was still
 * playing the PREVIOUS entry animation when the exit class is applied (e.g.
 * the caster pushes a new card faster than the ~0.4–0.7s entry completes),
 * the browser cancels that still-running entry animation and fires
 * `animationcancel` for IT — which arrives before the new exit animation's
 * own `animationstart`. Without a guard, this listener (armed immediately
 * before/around that cancel) would catch the dying entry's cancel and fire
 * the swap instantly, skipping the exit animation entirely. To prevent this,
 * `animationend`/`animationcancel` are only allowed to trigger `finish()`
 * AFTER this listener has observed the EXIT animation's own `animationstart`
 * on `frameEl` — the prior entry's cancel arrives before that point and is
 * correctly ignored. If the exit class is applied when nothing was
 * previously animating (the common case — the previous entry already
 * settled), the exit's own animationstart fires almost immediately, so this
 * adds no perceptible delay.
 *
 * Does NOT check `e.animationName` against a specific exit id — under
 * prefers-reduced-motion, some exit presets (slide-down, spin-away) are
 * overridden to a different keyframe name (fade-grow — see animations.css'
 * reduced-motion block), so name-matching would make this listener miss the
 * override. The reduced-motion override still emits animationstart/
 * animationend like any other CSS animation, so the started-gate works
 * unchanged under reduced motion.
 *
 * Returns a cleanup function that removes all listeners and cancels the
 * fallback timer. MUST be called from the owning effect's cleanup so a stale
 * callback can never fire after the effect that armed it has been superseded
 * by a newer card change.
 */
function armExitSwap(
  frameEl: HTMLElement,
  performSwap: () => void,
  fallbackMs: number,
): () => void {
  let done = false;
  let started = false;
  let fallbackTimerId = 0;

  const finish = () => {
    if (done) return;
    done = true;
    clearTimeout(fallbackTimerId);
    frameEl.removeEventListener('animationstart', onAnimStart);
    frameEl.removeEventListener('animationend', onAnimEndOrCancel);
    frameEl.removeEventListener('animationcancel', onAnimEndOrCancel);
    performSwap();
  };

  const onAnimStart = (e: AnimationEvent) => {
    if (e.target !== frameEl) return;
    // Marks that the EXIT animation itself has begun. Any animationend/
    // animationcancel observed before this point belongs to whatever was
    // animating previously (e.g. a still-running entry being cancelled by
    // the class change) and must not trigger the swap.
    started = true;
  };

  const onAnimEndOrCancel = (e: AnimationEvent) => {
    // Ignore bubbled animation events from descendants — only the card-frame
    // itself carries the exit animation class.
    if (e.target !== frameEl) return;
    // Ignore end/cancel events that arrive before the exit animation's own
    // start — these belong to a prior (now-superseded) animation.
    if (!started) return;
    finish();
  };

  frameEl.addEventListener('animationstart', onAnimStart);
  frameEl.addEventListener('animationend', onAnimEndOrCancel);
  // animationcancel fires if the exit animation is interrupted before
  // completing (e.g. its class is changed externally) — treat it the same as
  // animationend so the swap is never permanently stuck waiting on an event
  // that will not arrive.
  frameEl.addEventListener('animationcancel', onAnimEndOrCancel);
  fallbackTimerId = window.setTimeout(finish, fallbackMs);

  return () => {
    done = true;
    clearTimeout(fallbackTimerId);
    frameEl.removeEventListener('animationstart', onAnimStart);
    frameEl.removeEventListener('animationend', onAnimEndOrCancel);
    frameEl.removeEventListener('animationcancel', onAnimEndOrCancel);
  };
}

export function CardOverlay() {
  const { data, roomMeta, animationSettings } = useWsEvents<CardData>(
    'card',
    { imageUrl: null },
  );

  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  const [animClass, setAnimClass] = useState('');
  const prevImageUrl = useRef<string | null>(null);
  const cardBackImageRef = useRef<HTMLImageElement | null>(null);

  // Ref to the persistent card-frame element (never remounted, unlike the keyed
  // <img> inside it) — used by armExitSwap to listen for the real end of
  // whichever exit animation is currently playing on it.
  const cardFrameRef = useRef<HTMLDivElement | null>(null);

  // Keep a ref so setTimeout callbacks always read the latest settings
  // without animationSettings needing to be a useEffect dependency
  const animSettingsRef = useRef(animationSettings);
  animSettingsRef.current = animationSettings;

  // Tracks whether something meaningful is visible right now (card or card-back).
  // Used by the new-card branch to decide whether to play an exit animation first.
  // Updated on every render so setTimeout callbacks read the latest value.
  const somethingVisibleRef = useRef(false);

  // Callback set by each entry-animation branch just before mounting a new keyed
  // <img>. The <img>'s decode()/onLoad/onError signals call this to fire the entry
  // animation only once the DISPLAYED element's own bitmap is confirmed ready —
  // preventing the flash where the entry animation starts before the image paints.
  // Cleared in cleanup so stale signals from unmounted elements are ignored.
  const onImgReadyRef = useRef<(() => void) | null>(null);

  // Callback ref attached to the keyed <img>. Fires when React mounts the fresh
  // element (one mount per distinct showUrl, since the element is freshly keyed).
  // img.decode() resolves only when the bitmap is FULLY decoded and can be painted
  // without further delay — this is more reliable than the 'load' event, which on
  // some browsers can fire slightly before decode finishes for large/network images.
  // decode() also REJECTS on load failure or if the src changes before it settles,
  // so .catch() is the correct place to still fire the trigger (don't hang on a
  // broken image) rather than a place that indicates an unexpected bug.
  // onLoad/onError on the element (below, in JSX) are kept as a redundant backup
  // in case decode() is unsupported or never settles in some browser edge case —
  // harmless to call onImgReadyRef.current twice since triggerEntry is guarded by
  // a `triggered` flag in each branch.
  const handleImgRef = useCallback((node: HTMLImageElement | null) => {
    if (!node) return;
    void node
      .decode()
      .then(() => {
        onImgReadyRef.current?.();
      })
      .catch(() => {
        onImgReadyRef.current?.();
      });
  }, []);

  // Preload card-back image to prevent flash when card is removed
  useEffect(() => {
    const cardBackUrl = roomMeta?.cardBackImageUrl;
    if (!cardBackUrl) return;

    const img = new Image();
    img.src = cardBackUrl;
    cardBackImageRef.current = img;
  }, [roomMeta?.cardBackImageUrl]);

  // When card changes, animate entry/exit
  useEffect(() => {
    const newUrl = data.imageUrl;
    if (newUrl === prevImageUrl.current) return;

    if (!newUrl && prevImageUrl.current) {
      // Card removed — play exit animation, then animate the card-back in
      const exitId = resolveAnimId(animSettingsRef.current, 'cardExit');
      setAnimClass(`anim-${exitId}`);

      let triggered = false;
      let safetyTimerId = 0;
      let entryRafId = 0;

      // performSwap runs the instant the exit animation actually finishes
      // (armed below via armExitSwap), not at a fixed delay — so there is no
      // dead gap between the old card disappearing and the card-back appearing.
      const performSwap = () => {
        // Switch to card-back (or null if none configured) with ANIM_HIDDEN,
        // then gate the entry animation on the card-back <img>'s own decode().
        const cardBackUrl = roomMeta?.cardBackImageUrl ?? null;
        setAnimClass(ANIM_HIDDEN);
        setDisplayUrl(cardBackUrl);

        if (cardBackUrl) {
          const entryId = resolveAnimId(animSettingsRef.current, 'cardEntry');

          // triggerEntry: called by decode()/onLoad (or the long failsafe) on the
          // card-back <img> once its bitmap is ready. The inner RAF ensures
          // ANIM_HIDDEN is committed and painted before the entry class is applied.
          const triggerEntry = () => {
            if (triggered) return;
            triggered = true;
            clearTimeout(safetyTimerId);
            entryRafId = requestAnimationFrame(() => {
              setAnimClass(`anim-${entryId}`);
            });
          };

          onImgReadyRef.current = triggerEntry;
          safetyTimerId = window.setTimeout(triggerEntry, ENTRY_SAFETY_DELAY_MS);
        } else {
          // No card-back configured — nothing to animate in; clear the ref
          onImgReadyRef.current = null;
        }
      };

      const frameEl = cardFrameRef.current;
      const removeExitListener = frameEl
        ? armExitSwap(frameEl, performSwap, EXIT_FALLBACK_DELAY_MS)
        : null;
      // frameEl should always be attached here (the frame is already mounted
      // whenever a card is being removed) — this is a defensive fallback only.
      const fallbackOnlyTimer = removeExitListener ? 0 : window.setTimeout(performSwap, EXIT_FALLBACK_DELAY_MS);

      prevImageUrl.current = newUrl;
      return () => {
        triggered = true;
        removeExitListener?.();
        clearTimeout(fallbackOnlyTimer);
        clearTimeout(safetyTimerId);
        cancelAnimationFrame(entryRafId);
        onImgReadyRef.current = null;
      };
    }

    if (newUrl) {
      prevImageUrl.current = newUrl;

      // Warm the HTTP/decode cache while the exit animation plays. Fire-and-forget —
      // the swap is no longer gated on this promise. key={showUrl} ensures a fresh
      // <img> element mounts with no prior bitmap; the preload reduces how long the
      // freshly-keyed element's OWN decode() (the real gate — see handleImgRef)
      // takes to resolve, but the entry trigger never assumes any particular speed.
      const preloadImg = new Image();
      preloadImg.src = newUrl;
      void preloadImg.decode().catch(() => {
        // Ignore — the keyed <img> element will load/decode the URL independently
      });

      let cancelled = false;
      let triggered = false;
      let safetyTimerId = 0;
      let entryRafId = 0;

      // Build the entry trigger for this card. Called by decode()/onLoad (or the
      // long failsafe) on the freshly-keyed <img> — whenever its bitmap actually
      // becomes ready, however long that takes. The inner RAF ensures at least one
      // frame of ANIM_HIDDEN is committed and painted before the entry class is
      // applied, so the bitmap is guaranteed ready when the animation's first
      // keyframe runs — true for both cached (fast) and uncached (slow) loads.
      const buildTrigger = (entryId: string) => () => {
        if (cancelled || triggered) return;
        triggered = true;
        clearTimeout(safetyTimerId);
        entryRafId = requestAnimationFrame(() => {
          if (cancelled) return;
          setAnimClass(`anim-${entryId}`);
        });
      };

      if (somethingVisibleRef.current) {
        // Something is visible (previous card or card-back) — play exit first,
        // then swap in the new card. Preloading runs in parallel above.
        const exitId = resolveAnimId(animSettingsRef.current, 'cardExit');
        setAnimClass(`anim-${exitId}`);

        // performSwap fires the instant the actual exit animation completes
        // (armed below via armExitSwap) instead of at a fixed delay, so a short
        // exit preset (e.g. the default 0.3s fade-out) swaps immediately rather
        // than leaving a dead gap until a hardcoded 700ms elapses.
        const performSwap = () => {
          if (cancelled) return;
          // Mount the fresh keyed <img> behind ANIM_HIDDEN, then arm the
          // decode-gated trigger for the entry animation.
          setAnimClass(ANIM_HIDDEN);
          setDisplayUrl(newUrl);

          const entryId = resolveAnimId(animSettingsRef.current, 'cardEntry');
          const triggerEntry = buildTrigger(entryId);
          onImgReadyRef.current = triggerEntry;
          safetyTimerId = window.setTimeout(triggerEntry, ENTRY_SAFETY_DELAY_MS);
        };

        const frameEl = cardFrameRef.current;
        const removeExitListener = frameEl
          ? armExitSwap(frameEl, performSwap, EXIT_FALLBACK_DELAY_MS)
          : null;
        const fallbackOnlyTimer = removeExitListener ? 0 : window.setTimeout(performSwap, EXIT_FALLBACK_DELAY_MS);

        return () => {
          cancelled = true;
          triggered = true;
          removeExitListener?.();
          clearTimeout(fallbackOnlyTimer);
          clearTimeout(safetyTimerId);
          cancelAnimationFrame(entryRafId);
          onImgReadyRef.current = null;
        };
      } else {
        // Nothing visible yet — mount with ANIM_HIDDEN and arm entry trigger immediately.
        setAnimClass(ANIM_HIDDEN);
        setDisplayUrl(newUrl);

        const entryId = resolveAnimId(animSettingsRef.current, 'cardEntry');
        const triggerEntry = buildTrigger(entryId);
        onImgReadyRef.current = triggerEntry;
        safetyTimerId = window.setTimeout(triggerEntry, ENTRY_SAFETY_DELAY_MS);

        return () => {
          cancelled = true;
          triggered = true;
          clearTimeout(safetyTimerId);
          cancelAnimationFrame(entryRafId);
          onImgReadyRef.current = null;
        };
      }
    }
  }, [data.imageUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive card back URL from room meta
  const cardBackUrl = roomMeta?.cardBackImageUrl || null;

  // Track previous card back URL for game-switch animation
  const prevCardBackUrl = useRef<string | null>(null);

  // When card back changes (game switch or initial load) and no card is on stream,
  // animate the card back transition
  useEffect(() => {
    if (!cardBackUrl) return;
    const isInitialLoad = prevCardBackUrl.current === null;
    const hasChanged = cardBackUrl !== prevCardBackUrl.current;
    const noCardOnStream = data.imageUrl === null;

    if (hasChanged && noCardOnStream) {
      let triggered = false;
      let safetyTimerId = 0;
      let entryRafId = 0;

      // Same decode()-gated pattern as the card entry branches.
      const buildTrigger = (entryId: string) => () => {
        if (triggered) return;
        triggered = true;
        clearTimeout(safetyTimerId);
        entryRafId = requestAnimationFrame(() => {
          setAnimClass(`anim-${entryId}`);
        });
      };

      if (isInitialLoad) {
        // First card back arrival — mount behind ANIM_HIDDEN and arm entry trigger
        setAnimClass(ANIM_HIDDEN);
        setDisplayUrl(cardBackUrl);

        const entryId = resolveAnimId(animSettingsRef.current, 'cardEntry');
        const triggerEntry = buildTrigger(entryId);
        onImgReadyRef.current = triggerEntry;
        safetyTimerId = window.setTimeout(triggerEntry, ENTRY_SAFETY_DELAY_MS);

        prevCardBackUrl.current = cardBackUrl;
        return () => {
          triggered = true;
          clearTimeout(safetyTimerId);
          cancelAnimationFrame(entryRafId);
          onImgReadyRef.current = null;
        };
      } else {
        // Game switch — exit old card back, enter new one
        const exitId = resolveAnimId(animSettingsRef.current, 'cardExit');
        setAnimClass(`anim-${exitId}`);

        // performSwap fires on the real end of the exit animation (armed below),
        // not at a fixed delay — same fix as the other two exit branches.
        const performSwap = () => {
          setAnimClass(ANIM_HIDDEN);
          setDisplayUrl(cardBackUrl);

          const entryId = resolveAnimId(animSettingsRef.current, 'cardEntry');
          const triggerEntry = buildTrigger(entryId);
          onImgReadyRef.current = triggerEntry;
          safetyTimerId = window.setTimeout(triggerEntry, ENTRY_SAFETY_DELAY_MS);
        };

        const frameEl = cardFrameRef.current;
        const removeExitListener = frameEl
          ? armExitSwap(frameEl, performSwap, EXIT_FALLBACK_DELAY_MS)
          : null;
        const fallbackOnlyTimer = removeExitListener ? 0 : window.setTimeout(performSwap, EXIT_FALLBACK_DELAY_MS);

        prevCardBackUrl.current = cardBackUrl;
        return () => {
          triggered = true;
          removeExitListener?.();
          clearTimeout(fallbackOnlyTimer);
          clearTimeout(safetyTimerId);
          cancelAnimationFrame(entryRafId);
          onImgReadyRef.current = null;
        };
      }
    }
    prevCardBackUrl.current = cardBackUrl;
  }, [cardBackUrl, data.imageUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show actively displayed card, or fall back to card back
  const showUrl = displayUrl || cardBackUrl;

  // Keep ref in sync so the effect's setTimeout callbacks can read the latest value
  somethingVisibleRef.current = showUrl !== null;

  return (
    <div style={{
      width: '430px',
      height: '600px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'transparent',
      perspective: '1200px',
    }}>
      <div
        ref={cardFrameRef}
        className={`card-frame ${animClass}`}
        style={{
          position: 'relative',
          width: '430px',
          height: '600px',
          background: 'transparent',
          borderRadius: '12px',
          padding: 0,
          transformStyle: 'preserve-3d', // NOTE: do not add overflow:hidden — it breaks 3D card animations (flip-in, etc.)
          willChange: animClass ? 'transform, opacity' : 'auto',
        }}
      >
        {showUrl ? (
          // key={showUrl} forces React to unmount and remount the <img> on every URL
          // change, ensuring no previously-decoded old bitmap persists on the element.
          // handleImgRef (decode()) is the primary entry-animation trigger; onLoad/
          // onError are a redundant backup. Both route through onImgReadyRef so the
          // entry class is only applied once the displayed element's bitmap is
          // genuinely ready to paint — eliminating the race where the animation
          // starts before the image is visible, regardless of load speed.
          <img
            key={showUrl}
            ref={handleImgRef}
            src={showUrl}
            alt=""
            onLoad={() => { onImgReadyRef.current?.(); }}
            onError={() => { onImgReadyRef.current?.(); }}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              borderRadius: '12px',
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
